import { useEffect, useState, type ReactNode } from 'react';
import { getCompanionProfile, updateCompanionProfile } from '../api';
import { useActiveWorldStore } from '../store';
import { CompanionProfile } from '../types';
import { isMinorApparentAge } from '../utils/companion';

interface CompanionSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  worldId: string;
}

type CompanionRole = 'friend' | 'mentor' | 'coach' | 'confidant' | 'partner' | 'custom';
type CompanionMedium = NonNullable<CompanionProfile['communication_medium']>;

type ProfileForm = {
  name: string;
  userPreferredName: string;
  pronouns: string;
  apparentAge: string;
  role: CompanionRole;
  relationshipStance: string;
  communicationMedium: CompanionMedium;
  userDescription: string;
  speechPatterns: string;
  vocabularyTier: string;
  emotionalBaseline: string;
  communicationStyle: string;
  backstory: string;
  currentLifeSituation: string;
  occupation: string;
  interests: string;
  innerWorld: string;
  quirks: string;
  primaryDrive: string;
  attachmentStyle: string;
  coreWound: string;
  defenseMechanisms: string;
  blindSpots: string;
  hiddenDesires: string;
  fears: string;
  contradictions: string;
  formativeExperiences: string;
  personalValues: string;
  acknowledgeAi: boolean;
  allowRoleplay: boolean;
  allowRomance: boolean;
  safetyMode: 'standard' | 'permissive';
  topicsToAvoid: string;
};

const EMPTY_FORM: ProfileForm = {
  name: '', userPreferredName: '', pronouns: '', apparentAge: '', role: 'friend',
  relationshipStance: '', communicationMedium: 'text_message', userDescription: '',
  speechPatterns: '', vocabularyTier: '', emotionalBaseline: '', communicationStyle: '',
  backstory: '', currentLifeSituation: '', occupation: '', interests: '', innerWorld: '', quirks: '',
  primaryDrive: '', attachmentStyle: '', coreWound: '', defenseMechanisms: '', blindSpots: '',
  hiddenDesires: '', fears: '', contradictions: '', formativeExperiences: '', personalValues: '',
  acknowledgeAi: true, allowRoleplay: false, allowRomance: false, safetyMode: 'standard', topicsToAvoid: '',
};

const ROLE_OPTIONS: CompanionRole[] = ['friend', 'mentor', 'coach', 'confidant', 'partner', 'custom'];
const MEDIUM_OPTIONS: Array<{ value: CompanionMedium; label: string }> = [
  { value: 'text_message', label: 'Text Messages' },
  { value: 'chat_app', label: 'Chat App' },
  { value: 'social_dm', label: 'Social DMs' },
  { value: 'voice_call', label: 'Voice Call' },
  { value: 'video_call', label: 'Video Call' },
  { value: 'in_person', label: 'In Person' },
  { value: 'letters', label: 'Letters' },
  { value: 'journal', label: 'Journal' },
];

function parseBoundaries(raw: string | null | undefined) {
  try {
    const parsed = JSON.parse(raw ?? '{}') as Partial<{
      allowRoleplay: boolean;
      allowRomance: boolean;
      safetyMode: 'standard' | 'permissive';
      topicsToAvoid: string[];
    }>;
    return {
      allowRoleplay: parsed.allowRoleplay ?? false,
      allowRomance: parsed.allowRomance ?? false,
      safetyMode: parsed.safetyMode === 'permissive' ? 'permissive' as const : 'standard' as const,
      topicsToAvoid: (parsed.topicsToAvoid ?? []).join(', '),
    };
  } catch {
    return { allowRoleplay: false, allowRomance: false, safetyMode: 'standard' as const, topicsToAvoid: '' };
  }
}

function lines(values: string[] | null | undefined): string {
  return (values ?? []).join('\n');
}

function splitLines(value: string): string[] {
  return value.split('\n').map((item) => item.trim()).filter(Boolean);
}

function formFromProfile(profile: CompanionProfile): ProfileForm {
  const boundaries = parseBoundaries(profile.boundaries_json);
  return {
    name: profile.name,
    userPreferredName: profile.user_preferred_name ?? '',
    pronouns: profile.pronouns ?? '',
    apparentAge: profile.apparent_age ?? '',
    role: (ROLE_OPTIONS.includes(profile.role as CompanionRole) ? profile.role : 'friend') as CompanionRole,
    relationshipStance: profile.relationship_stance ?? '',
    communicationMedium: profile.communication_medium ?? 'text_message',
    userDescription: profile.user_description ?? '',
    speechPatterns: profile.speech_patterns ?? '',
    vocabularyTier: profile.vocabulary_tier ?? '',
    emotionalBaseline: profile.emotional_baseline ?? '',
    communicationStyle: profile.communication_style ?? '',
    backstory: profile.backstory ?? '',
    currentLifeSituation: profile.current_life_situation ?? '',
    occupation: profile.occupation ?? '',
    interests: lines(profile.interests),
    innerWorld: profile.inner_world ?? '',
    quirks: lines(profile.quirks),
    primaryDrive: profile.primary_drive ?? '',
    attachmentStyle: profile.attachment_style ?? '',
    coreWound: profile.core_wound ?? '',
    defenseMechanisms: lines(profile.defense_mechanisms),
    blindSpots: lines(profile.blind_spots),
    hiddenDesires: lines(profile.hidden_desires),
    fears: lines(profile.fears),
    contradictions: lines(profile.contradictions),
    formativeExperiences: lines(profile.formative_experiences),
    personalValues: lines(profile.personal_values),
    acknowledgeAi: profile.acknowledge_ai === undefined ? true : Boolean(profile.acknowledge_ai),
    ...boundaries,
  };
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string }) {
  return (
    <label className="block text-sm text-dark-200">
      <span className="block mb-1">{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className="narrative-input w-full px-3 py-2" />
    </label>
  );
}

function Area({ label, value, onChange, rows = 3, hint }: { label: string; value: string; onChange: (value: string) => void; rows?: number; hint?: string }) {
  return (
    <label className="block text-sm text-dark-200">
      <span className="block mb-1">{label}</span>
      {hint && <span className="block text-xs text-dark-400 mb-1">{hint}</span>}
      <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={rows} className="narrative-input w-full px-3 py-2" />
    </label>
  );
}

function Section({ title, children, open = false }: { title: string; children: ReactNode; open?: boolean }) {
  return (
    <details open={open} className="rounded-lg border border-dark-600 bg-dark-800/60">
      <summary className="cursor-pointer px-4 py-3 font-medium text-dark-100">{title}</summary>
      <div className="px-4 pb-4 space-y-4">{children}</div>
    </details>
  );
}

export function CompanionSettingsModal({ isOpen, onClose, worldId }: CompanionSettingsModalProps) {
  const setCompanionProfile = useActiveWorldStore((state) => state.setCompanionProfile);
  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const update = <K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) => setForm((current) => ({ ...current, [key]: value }));
  const isMinor = isMinorApparentAge(form.apparentAge);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    setError(null);
    getCompanionProfile(worldId)
      .then((result) => {
        if (!result.profile) throw new Error('Companion profile not found');
        setForm(formFromProfile(result.profile));
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : 'Failed to load profile'))
      .finally(() => setIsLoading(false));
  }, [isOpen, worldId]);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await updateCompanionProfile(worldId, {
        name: form.name.trim(),
        userPreferredName: form.userPreferredName.trim() || null,
        pronouns: form.pronouns.trim() || null,
        apparentAge: form.apparentAge.trim() || null,
        role: form.role,
        relationshipStance: form.relationshipStance.trim() || null,
        communicationMedium: form.communicationMedium,
        userDescription: form.userDescription.trim() || null,
        speechPatterns: form.speechPatterns.trim() || null,
        vocabularyTier: form.vocabularyTier.trim() || null,
        emotionalBaseline: form.emotionalBaseline.trim() || null,
        communicationStyle: form.communicationStyle.trim() || null,
        backstory: form.backstory.trim() || null,
        currentLifeSituation: form.currentLifeSituation.trim() || null,
        occupation: form.occupation.trim() || null,
        interests: splitLines(form.interests),
        innerWorld: form.innerWorld.trim() || null,
        quirks: splitLines(form.quirks),
        primaryDrive: form.primaryDrive.trim() || null,
        attachmentStyle: form.attachmentStyle.trim() || null,
        coreWound: form.coreWound.trim() || null,
        defenseMechanisms: splitLines(form.defenseMechanisms),
        blindSpots: splitLines(form.blindSpots),
        hiddenDesires: splitLines(form.hiddenDesires),
        fears: splitLines(form.fears),
        contradictions: splitLines(form.contradictions),
        formativeExperiences: splitLines(form.formativeExperiences),
        personalValues: splitLines(form.personalValues),
        acknowledgeAi: form.acknowledgeAi,
        boundaries: {
          allowRoleplay: form.allowRoleplay,
          allowRomance: isMinor ? false : form.allowRomance,
          safetyMode: isMinor ? 'standard' : form.safetyMode,
          topicsToAvoid: form.topicsToAvoid.split(',').map((item) => item.trim()).filter(Boolean),
        },
      });
      setCompanionProfile(result.profile);
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-dark-900 rounded-xl shadow-2xl w-full max-w-3xl mx-4 border border-dark-600 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
          <div>
            <h2 className="text-xl font-semibold text-dark-50">Companion Profile</h2>
            <p className="text-xs text-dark-400 mt-1">Identity, medium, voice, psychology, and boundaries all feed the next response.</p>
          </div>
          <button onClick={onClose} className="text-dark-400 hover:text-dark-200 transition-colors">✕</button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          {error && <div className="text-red-400 text-sm">{error}</div>}
          {isLoading ? <div className="text-dark-300 text-sm">Loading profile…</div> : <>
            <Section title="Identity and medium" open>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name" value={form.name} onChange={(value) => update('name', value)} />
                <Field label="What they call you" value={form.userPreferredName} onChange={(value) => update('userPreferredName', value)} />
                <Field label="Pronouns" value={form.pronouns} onChange={(value) => update('pronouns', value)} placeholder="she/her" />
                <Field label="Age" value={form.apparentAge} onChange={(value) => update('apparentAge', value)} />
                <label className="block text-sm text-dark-200"><span className="block mb-1">Role</span><select value={form.role} onChange={(event) => update('role', event.target.value as CompanionRole)} className="narrative-input w-full px-3 py-2">{ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}</select></label>
                <label className="block text-sm text-dark-200"><span className="block mb-1">Communication medium</span><select value={form.communicationMedium} onChange={(event) => update('communicationMedium', event.target.value as CompanionMedium)} className="narrative-input w-full px-3 py-2">{MEDIUM_OPTIONS.map((medium) => <option key={medium.value} value={medium.value}>{medium.label}</option>)}</select></label>
              </div>
              <Field label="Relationship stance" value={form.relationshipStance} onChange={(value) => update('relationshipStance', value)} />
              <Area label="Original description" value={form.userDescription} onChange={(value) => update('userDescription', value)} rows={4} />
            </Section>

            <Section title="Voice">
              <Area label="Speech patterns" value={form.speechPatterns} onChange={(value) => update('speechPatterns', value)} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><Field label="Vocabulary tier" value={form.vocabularyTier} onChange={(value) => update('vocabularyTier', value)} /><Field label="Emotional baseline" value={form.emotionalBaseline} onChange={(value) => update('emotionalBaseline', value)} /></div>
              <Area label="Communication style" value={form.communicationStyle} onChange={(value) => update('communicationStyle', value)} />
            </Section>

            <Section title="Life and interests">
              <Area label="Backstory" value={form.backstory} onChange={(value) => update('backstory', value)} rows={6} />
              <Area label="Current life situation" value={form.currentLifeSituation} onChange={(value) => update('currentLifeSituation', value)} rows={4} />
              <Field label="Occupation" value={form.occupation} onChange={(value) => update('occupation', value)} />
              <Area label="Interests" value={form.interests} onChange={(value) => update('interests', value)} hint="One item per line" />
              <Area label="Inner world" value={form.innerWorld} onChange={(value) => update('innerWorld', value)} rows={4} />
              <Area label="Quirks" value={form.quirks} onChange={(value) => update('quirks', value)} hint="One item per line" />
            </Section>

            <Section title="Psychology">
              <Area label="Primary drive" value={form.primaryDrive} onChange={(value) => update('primaryDrive', value)} />
              <Field label="Attachment style" value={form.attachmentStyle} onChange={(value) => update('attachmentStyle', value)} />
              <Area label="Core wound" value={form.coreWound} onChange={(value) => update('coreWound', value)} rows={4} />
              <Area label="Defense mechanisms" value={form.defenseMechanisms} onChange={(value) => update('defenseMechanisms', value)} hint="One item per line" />
              <Area label="Blind spots" value={form.blindSpots} onChange={(value) => update('blindSpots', value)} hint="One item per line" />
              <Area label="Hidden desires" value={form.hiddenDesires} onChange={(value) => update('hiddenDesires', value)} hint="One item per line" />
              <Area label="Fears" value={form.fears} onChange={(value) => update('fears', value)} hint="One item per line" />
              <Area label="Contradictions" value={form.contradictions} onChange={(value) => update('contradictions', value)} hint="One item per line" />
              <Area label="Formative experiences" value={form.formativeExperiences} onChange={(value) => update('formativeExperiences', value)} hint="One item per line" />
              <Area label="Personal values" value={form.personalValues} onChange={(value) => update('personalValues', value)} hint="One item per line" />
            </Section>

            <Section title="Boundaries">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-dark-200"><input type="checkbox" checked={form.allowRoleplay} onChange={(event) => update('allowRoleplay', event.target.checked)} />Allow roleplay</label>
                <label className="flex items-center gap-2 text-sm text-dark-200"><input type="checkbox" checked={!isMinor && form.allowRomance} disabled={isMinor} onChange={(event) => update('allowRomance', event.target.checked)} />Allow romance</label>
                <label className="flex items-center gap-2 text-sm text-dark-200"><input type="checkbox" checked={form.acknowledgeAi} onChange={(event) => update('acknowledgeAi', event.target.checked)} />Acknowledge AI origin when asked</label>
                <label className="block text-sm text-dark-200"><span className="block mb-1">Safety mode</span><select value={isMinor ? 'standard' : form.safetyMode} disabled={isMinor} onChange={(event) => update('safetyMode', event.target.value as ProfileForm['safetyMode'])} className="narrative-input w-full px-3 py-2"><option value="standard">Standard</option><option value="permissive">Permissive</option></select></label>
              </div>
              {isMinor && <p className="text-xs text-amber-300">Profiles under 18 are locked to standard, non-romantic interaction.</p>}
              <Field label="Topics to avoid" value={form.topicsToAvoid} onChange={(value) => update('topicsToAvoid', value)} placeholder="Comma-separated" />
            </Section>
          </>}
        </div>

        <div className="px-6 py-4 border-t border-dark-600 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} className="btn-primary" disabled={isLoading || isSaving || !form.name.trim()}>{isSaving ? 'Saving…' : 'Save Profile'}</button>
        </div>
      </div>
    </div>
  );
}
